import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { AuthContext } from "../AuthContext";
import { useTranslation } from "../i18n/LanguageContext";
import { normalizeMessage } from "../utils/chat";

// A minimal, client-only "chat notifications" layer built on top of the
// EXISTING chats/{chatId} schema (members: [uidA, uidB], messages: []) —
// no new Firestore fields, no Firestore rules changes. Read/unread state is
// tracked locally per signed-in user (localStorage), which is the smallest
// safe extension possible: it needs no new writable field (so nothing new
// for firestore.rules to protect), it can't be spoofed by another user
// (it's never written to Firestore), and it fully satisfies the required UX
// (toast + badge + click-to-open) without a second messaging architecture.
// Trade-off (documented, not hidden): unread state is per-browser/device,
// not synced across multiple devices for the same account.
const ChatNotificationsContext = createContext(null);

export function useChatNotifications() {
  return useContext(ChatNotificationsContext);
}

const lastSeenStorageKey = (uid, chatId) => `partybuddy.chat.lastSeen.${uid}.${chatId}`;

const readLastSeenCount = (uid, chatId) => {
  try {
    const raw = window.localStorage.getItem(lastSeenStorageKey(uid, chatId));
    const parsed = raw == null ? 0 : parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  } catch {
    return 0;
  }
};

const writeLastSeenCount = (uid, chatId, count) => {
  try {
    window.localStorage.setItem(lastSeenStorageKey(uid, chatId), String(count));
  } catch {
    // localStorage may be unavailable (private mode) — unread badges simply
    // won't persist across reloads in that case; not fatal.
  }
};

let toastIdCounter = 0;

export function ChatNotificationsProvider({ children }) {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // { [otherUid]: { chatId, unreadCount, messageCount } }
  const [chatState, setChatState] = useState({});
  const [toasts, setToasts] = useState([]);

  // The buddy uid of whichever chat is currently "on screen" — either via
  // the dedicated /chat/:buddyId route, or the embedded chat panel on the
  // Buddies page (which sets this explicitly, see Buddies.js). Messages for
  // the active chat never produce a toast and are auto-marked read.
  const activeChatUidRef = useRef(null);
  // Tracks message counts we've already accounted for, per chatId, so we
  // only toast for messages appended AFTER the listener attached — not the
  // entire pre-existing history on first load/page refresh.
  const seenCountsRef = useRef({});
  const buddyInfoCacheRef = useRef({});

  const setActiveChatBuddy = useCallback((otherUid) => {
    activeChatUidRef.current = otherUid || null;
  }, []);

  // Keep the ref in sync with the dedicated chat route too, so navigating to
  // /chat/:buddyId (e.g. from a notification click) suppresses/mark-reads
  // that chat even before ChatPage.js's own effect runs.
  useEffect(() => {
    const match = location.pathname.match(/^\/chat\/([^/]+)$/);
    if (match) setActiveChatBuddy(match[1]);
  }, [location.pathname, setActiveChatBuddy]);

  const markChatRead = useCallback(
    (chatId, otherUid, messageCount) => {
      if (!user || !chatId) return;
      writeLastSeenCount(user.uid, chatId, messageCount);
      setChatState((prev) => {
        if (!prev[otherUid]) return prev;
        return { ...prev, [otherUid]: { ...prev[otherUid], unreadCount: 0 } };
      });
    },
    [user]
  );

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const openChatFromToast = useCallback(
    (toast) => {
      dismissToast(toast.id);
      setActiveChatBuddy(toast.otherUid);
      markChatRead(toast.chatId, toast.otherUid, toast.messageCount);
      navigate(`/chat/${toast.otherUid}`);
    },
    [dismissToast, markChatRead, navigate, setActiveChatBuddy]
  );

  useEffect(() => {
    if (!user) {
      setChatState({});
      setToasts([]);
      seenCountsRef.current = {};
      return undefined;
    }

    const q = query(collection(db, "chats"), where("members", "array-contains", user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const nextState = {};

      snapshot.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const members = data.members || [];
        const otherUid = members.find((m) => m !== user.uid);
        if (!otherUid) return;

        const messages = data.messages || [];
        const chatId = docSnap.id;
        const isActive =
          activeChatUidRef.current === otherUid || location.pathname === `/chat/${otherUid}`;

        if (isActive) {
          // User is currently looking at this exact chat — keep it marked
          // read as new messages arrive instead of accumulating unread.
          writeLastSeenCount(user.uid, chatId, messages.length);
        }

        const lastSeenCount = readLastSeenCount(user.uid, chatId);
        const unreadCount = messages
          .slice(lastSeenCount)
          .filter((m) => m && m.senderId !== user.uid).length;

        nextState[otherUid] = { chatId, unreadCount, messageCount: messages.length };

        const lastRawMessage = messages.length ? messages[messages.length - 1] : null;
        if (lastRawMessage) {
          const normalized = normalizeMessage(lastRawMessage);
          const ts = normalized.timestamp;
          nextState[otherUid].lastMessage = normalized;
          nextState[otherUid].lastMessageAtMillis =
            ts?.toMillis?.() ?? (ts ? new Date(ts).getTime() : 0);
        } else {
          nextState[otherUid].lastMessage = null;
          nextState[otherUid].lastMessageAtMillis = 0;
        }

        const previouslySeenLength = seenCountsRef.current[chatId];
        seenCountsRef.current[chatId] = messages.length;

        // Only toast for genuinely new messages appended after this
        // listener attached (not the whole history on initial load), from
        // someone else, while this chat isn't the one currently open.
        if (
          previouslySeenLength !== undefined &&
          messages.length > previouslySeenLength &&
          !isActive
        ) {
          const newMessages = messages.slice(previouslySeenLength);
          const incoming = newMessages.filter((m) => m && m.senderId !== user.uid);
          if (incoming.length > 0) {
            const lastIncoming = normalizeMessage(incoming[incoming.length - 1]);
            (async () => {
              let buddyInfo = buddyInfoCacheRef.current[otherUid];
              if (!buddyInfo) {
                try {
                  const buddySnap = await getDoc(doc(db, "users", otherUid));
                  buddyInfo = buddySnap.exists()
                    ? { name: buddySnap.data().username || "", photoUrl: buddySnap.data().photoUrl || "" }
                    : { name: "", photoUrl: "" };
                } catch {
                  buddyInfo = { name: "", photoUrl: "" };
                }
                buddyInfoCacheRef.current[otherUid] = buddyInfo;
              }

              const body =
                lastIncoming.type === "event"
                  ? t("chat.eventSharedPreview", "Event shared")
                  : (lastIncoming.text || t("chat.newMessagePreviewFallback", "New message")).slice(0, 120);

              toastIdCounter += 1;
              setToasts((prev) => [
                ...prev,
                {
                  id: toastIdCounter,
                  chatId,
                  otherUid,
                  messageCount: messages.length,
                  title: buddyInfo.name || t("chat.title", "Chat"),
                  photoUrl: buddyInfo.photoUrl,
                  body,
                },
              ]);
            })();
          }
        }
      });

      setChatState(nextState);
    });

    return () => unsubscribe();
    // location.pathname is intentionally included so switching into/out of a
    // /chat/:buddyId route re-evaluates which chat is "active" immediately.
  }, [user, location.pathname, t]);

  // Auto-dismiss toasts after a few seconds so they never pile up.
  useEffect(() => {
    if (toasts.length === 0) return undefined;
    const timers = toasts.map((toast) =>
      setTimeout(() => dismissToast(toast.id), 6000)
    );
    return () => timers.forEach(clearTimeout);
  }, [toasts, dismissToast]);

  const totalUnread = useMemo(
    () => Object.values(chatState).reduce((sum, entry) => sum + (entry.unreadCount || 0), 0),
    [chatState]
  );

  const unreadByBuddy = useMemo(() => {
    const map = {};
    Object.entries(chatState).forEach(([otherUid, entry]) => {
      map[otherUid] = entry.unreadCount || 0;
    });
    return map;
  }, [chatState]);

  // Richer per-buddy chat info (chatId, last message, unread count) for the
  // Buddies list UI, so it never needs its own separate `chats` listener —
  // this context's single onSnapshot is the one source of truth.
  const chatsByBuddy = chatState;

  const value = useMemo(
    () => ({
      totalUnread,
      unreadByBuddy,
      chatsByBuddy,
      setActiveChatBuddy,
      markChatRead: (otherUid) => {
        const entry = chatState[otherUid];
        if (entry) markChatRead(entry.chatId, otherUid, entry.messageCount);
      },
    }),
    [totalUnread, unreadByBuddy, chatsByBuddy, setActiveChatBuddy, markChatRead, chatState]
  );

  return (
    <ChatNotificationsContext.Provider value={value}>
      {children}
      {/* Toast overlay — mounted once here so any page (not just chat pages)
          shows incoming-message notifications. */}
      {toasts.length > 0 && (
        <div className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex flex-col items-center gap-2 px-4">
          {toasts.map((toast) => (
            <button
              key={toast.id}
              type="button"
              onClick={() => openChatFromToast(toast)}
              className="pointer-events-auto w-full max-w-sm rounded-xl border border-emerald-500/40 bg-slate-900/95 p-3 text-left text-white shadow-2xl backdrop-blur-sm transition hover:border-emerald-400/70 hover:bg-slate-800/95"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-emerald-300">{toast.title}</p>
                  <p className="mt-0.5 truncate text-sm text-slate-200">{toast.body}</p>
                </div>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    dismissToast(toast.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      dismissToast(toast.id);
                    }
                  }}
                  className="shrink-0 rounded-full px-2 text-slate-400 hover:text-slate-200"
                  aria-label="Dismiss"
                >
                  ✕
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </ChatNotificationsContext.Provider>
  );
}
